import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";

export type GlobalIntent = "global_curious" | "global_dating" | "global_serious";

export const ALL_INTENTS: GlobalIntent[] = ["global_curious", "global_dating", "global_serious"];

export interface GlobalFilterPreferences {
  ageMin?: number;
  ageMax?: number;
  languages?: string[];
  verifiedOnly?: boolean;
  onlineOnly?: boolean;
  nearbyEnabled?: boolean;
  nearbyRadius?: number;
  countryCode?: string;
  stateId?: string;
  cityId?: string;
}

const STORAGE_KEY = "global_connect_intents";
const FILTER_STORAGE_KEY = "global_connect_filters";

export const useGlobalIntents = () => {
  const { user } = useAuthContext();
  const [selectedIntents, setSelectedIntents] = useState<GlobalIntent[]>([]);
  const [filterPreferences, setFilterPreferences] = useState<GlobalFilterPreferences>({});
  const [loading, setLoading] = useState(true);
  const [hasIntents, setHasIntents] = useState(false);

  // Load intents from localStorage first, then sync with DB
  useEffect(() => {
    const loadIntents = async () => {
      // Try localStorage first for instant UI
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as GlobalIntent[];
          setSelectedIntents(parsed);
          setHasIntents(parsed.length > 0);
        } catch {
          // Invalid cache, ignore
        }
      }

      const cachedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      if (cachedFilters) {
        try {
          setFilterPreferences(JSON.parse(cachedFilters));
        } catch {
          // Invalid cache, ignore
        }
      }

      // If logged in, fetch from DB
      if (user) {
        const { data, error } = await supabase
          .from("profiles")
          .select("global_intents, global_filter_preferences")
          .eq("id", user.id)
          .single();

        if (!error && data) {
          const dbIntents = (data.global_intents as GlobalIntent[]) || [];
          const dbFilters = (data.global_filter_preferences as GlobalFilterPreferences) || {};
          
          setSelectedIntents(dbIntents);
          setHasIntents(dbIntents.length > 0);
          setFilterPreferences(dbFilters);

          // Update localStorage cache
          if (dbIntents.length > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dbIntents));
          }
          if (Object.keys(dbFilters).length > 0) {
            localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(dbFilters));
          }
        }
      }

      setLoading(false);
    };

    loadIntents();
  }, [user]);

  // Save intents to DB and localStorage
  const saveIntents = useCallback(
    async (intents: GlobalIntent[]) => {
      if (!user) return { error: new Error("Not authenticated") };

      // Update local state immediately
      setSelectedIntents(intents);
      setHasIntents(intents.length > 0);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(intents));

      // Persist to DB
      const { error } = await supabase
        .from("profiles")
        .update({ global_intents: intents })
        .eq("id", user.id);

      if (error) {
        toast.error("Failed to save intents");
        return { error };
      }

      toast.success("Intents updated!");
      return { error: null };
    },
    [user]
  );

  // Save filter preferences
  const saveFilterPreferences = useCallback(
    async (filters: GlobalFilterPreferences) => {
      if (!user) return { error: new Error("Not authenticated") };

      setFilterPreferences(filters);
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));

      // Cast to any for JSONB compatibility with Supabase
      const { error } = await supabase
        .from("profiles")
        .update({ global_filter_preferences: JSON.parse(JSON.stringify(filters)) })
        .eq("id", user.id);

      if (error) {
        console.error("Failed to save filter preferences:", error);
        return { error };
      }

      return { error: null };
    },
    [user]
  );

  // Toggle a single intent
  const toggleIntent = useCallback((intent: GlobalIntent) => {
    setSelectedIntents((prev) => {
      if (prev.includes(intent)) {
        return prev.filter((i) => i !== intent);
      }
      return [...prev, intent];
    });
  }, []);

  // Select all intents
  const selectAll = useCallback(() => {
    setSelectedIntents([...ALL_INTENTS]);
  }, []);

  // Clear all intents
  const clearAll = useCallback(() => {
    setSelectedIntents([]);
  }, []);

  // Check if all are selected
  const isAllSelected = selectedIntents.length === ALL_INTENTS.length;

  return {
    selectedIntents,
    setSelectedIntents,
    filterPreferences,
    setFilterPreferences,
    loading,
    hasIntents,
    saveIntents,
    saveFilterPreferences,
    toggleIntent,
    selectAll,
    clearAll,
    isAllSelected,
  };
};

// Intent display helpers
export const intentLabels: Record<GlobalIntent, string> = {
  global_curious: "Global Curious",
  global_dating: "Global Dating",
  global_serious: "Global Serious",
};

export const intentDescriptions: Record<GlobalIntent, string> = {
  global_curious: "Open to chatting and cultural exchange",
  global_dating: "Open to long-distance romantic connection",
  global_serious: "Open to serious relationships across borders",
};

export const intentIcons: Record<GlobalIntent, string> = {
  global_curious: "🌍",
  global_dating: "💬",
  global_serious: "💍",
};
